import { forwardRef, useEffect, useRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/lib/cx';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  /** 一部選択（表の全選択などで使用） */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate, className, ...rest },
  ref,
) {
  const inner = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <label className={cx('bs-check', rest.disabled && 'is-disabled', className)}>
      <input
        ref={(el) => {
          inner.current = el;
          if (typeof ref === 'function') ref(el);
          else if (ref) ref.current = el;
        }}
        type="checkbox"
        className="bs-check__input"
        {...rest}
      />
      <span className="bs-check__box" aria-hidden />
      {label != null && <span className="bs-check__label">{label}</span>}
    </label>
  );
});

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio({ label, className, ...rest }, ref) {
  return (
    <label className={cx('bs-check bs-radio', rest.disabled && 'is-disabled', className)}>
      <input ref={ref} type="radio" className="bs-check__input" {...rest} />
      <span className="bs-check__box" aria-hidden />
      {label != null && <span className="bs-check__label">{label}</span>}
    </label>
  );
});

export interface RadioGroupProps<V extends string> {
  name: string;
  value: V | null | undefined;
  onChange: (value: V) => void;
  options: ReadonlyArray<{ value: V; label: ReactNode; disabled?: boolean }>;
  direction?: 'row' | 'column';
  disabled?: boolean;
  'aria-labelledby'?: string;
}

/** 制御コンポーネント版ラジオ群（react-hook-form では Controller と組み合わせる） */
export function RadioGroup<V extends string>({
  name,
  value,
  onChange,
  options,
  direction = 'row',
  disabled,
  ...aria
}: RadioGroupProps<V>) {
  return (
    <div role="radiogroup" className={cx('bs-choice-group', `bs-choice-group--${direction}`)} {...aria}>
      {options.map((o) => (
        <Radio
          key={o.value}
          name={name}
          value={o.value}
          label={o.label}
          checked={value === o.value}
          disabled={disabled || o.disabled}
          onChange={() => onChange(o.value)}
        />
      ))}
    </div>
  );
}
